import * as React from 'react';
import { AvailablePhotographer, AvailableTimeslot } from '../../types';
import {
    getInitialPhotographerId,
    getPersistedPhotographer,
    persistPhotographer
} from '../state/photographerUtils';
import { useBookingCalendarHeightOffset } from '../../state/useHeightOffsetHelper';
import PhotographerSection from '../../potographerSection/view/PhotographerSection';
import { NoPhotographersAvailable } from '../../potographerSection/view/NoPhotographersAvailable';
import { BookingDateSelect } from '../../bookingDateSelect/view/BookingDateSelect';
import { getAvailableTimeslotsPerHour } from '../../availableTimeslotsSection/state/availableTimeslotHelpers';
import { AvailableTimeslotsSection } from '../../availableTimeslotsSection/view/AvailableTimeslotsSection';
import { BookingFooter } from './BookingFooter';
import PortfolioModal from '../../../../../../photographerPortfolio/PortfolioModal';
import { NoAvailableTimeslots } from './NoAvailableTimeslots';

type PhotographerCentricProps = {
    availablePhotographers: AvailablePhotographer[];
    displayingBookingDate: Date;
    handleBookFactory: (
        planOrderId: number | null,
        photographerId: number | null
    ) => (timeslot: string) => void;
    onChangeDisplayingBookingDate: (dateTime: Date) => void;
    planOrderId: number | null;
    showHints: boolean;
    earliestBookableDate?: Date | null;
};

function getCurrentPhotographer(
    availablePhotographers: AvailablePhotographer[],
    selectedPhotographerId: number | null
) {
    if (selectedPhotographerId) {
        return availablePhotographers.find((p) => p.id === selectedPhotographerId);
    }

    let currentPhotographer = availablePhotographers.find((p) => p.availableTimeslots.length > 0);

    if (currentPhotographer) {
        return currentPhotographer;
    }

    if (availablePhotographers.length > 0) {
        return availablePhotographers[0];
    }

    return null;
}

export function PhotographerCentric({
    availablePhotographers,
    displayingBookingDate,
    handleBookFactory,
    onChangeDisplayingBookingDate,
    planOrderId,
    showHints,
    earliestBookableDate = null
}: PhotographerCentricProps) {
    let [selectedPhotographerId, setSelectedPhotographerId] = React.useState<number | null>(
        getInitialPhotographerId()
    );
    let [showPortfolioModal, setShowPortfolioModal] = React.useState(false);
    let [showFooter, setShowFooter] = React.useState<boolean>(false);
    let [selectedBookingDate, setSelectedBookingDate] = React.useState<string | null>(null);
    let [selectedPhotographer, setSelectedPhotographer] = React.useState<string | null>(null);
    let [selectedTimeslot, setSelectedTimeslot] = React.useState<AvailableTimeslot | null>(null);

    let { heightOffset, footerRef } = useBookingCalendarHeightOffset(showFooter);

    function handleChangeTimeslot(timeslot: string) {
        if (availablePhotographers.length < 1) {
            return;
        }

        // TODO: how do we handle null here?
        let photographer = availablePhotographers.find((p) => p.id === selectedPhotographerId);
        setSelectedPhotographer(photographer.name);

        setSelectedBookingDate(timeslot);
        setSelectedTimeslot(null);

        if (!showFooter) {
            setShowFooter(true);
        }
    }

    function handleChangeTimeslotFromFooter(timeslot: AvailableTimeslot) {
        setSelectedTimeslot(timeslot);
    }

    function handleCancelBook() {
        setShowFooter(false);

        // Delay a bit so that the footer can animate out before changing ui
        setTimeout(() => {
            setSelectedBookingDate(null);
            setSelectedTimeslot(null);
            setSelectedPhotographer(null);
        }, 0);
    }

    let currentPhotographer = getCurrentPhotographer(
        availablePhotographers,
        selectedPhotographerId
    );

    let timeslots = currentPhotographer
        ? currentPhotographer.availableTimeslots
        : ([] as AvailableTimeslot[]);
    let availableTimeslotsPerHour = getAvailableTimeslotsPerHour(timeslots);

    let [selectedHour] = selectedBookingDate ? selectedBookingDate.split(':') : [null, null];
    let availableTimeslotsForSelectedHour = availableTimeslotsPerHour.get(selectedHour) || [];

    let selectedForecast =
        availableTimeslotsForSelectedHour.length > 0
            ? availableTimeslotsForSelectedHour[0].forecast
            : null;

    let currentIndex = availablePhotographers.findIndex((p) => p.id === selectedPhotographerId);

    let persistedPhotographer = getPersistedPhotographer();

    return (
        <>
            {availablePhotographers.length > 0 && currentPhotographer ? (
                <PhotographerSection
                    showHint={showHints}
                    availablePhotographers={availablePhotographers}
                    disabled={showFooter}
                    photographer={
                        persistedPhotographer
                            ? {
                                  name: persistedPhotographer.name,
                                  profileImageReference: persistedPhotographer.avatarRef
                              }
                            : null
                    }
                    photographerIndicatorLabel={`${
                        persistedPhotographer === null ? 0 : currentIndex + 1
                    } / ${availablePhotographers.length}`}
                    leftDisabled={currentIndex === 0}
                    onLeftClick={() => {
                        if (currentIndex > 0) {
                            let previousPhotographer = availablePhotographers[currentIndex - 1];
                            persistPhotographer(
                                previousPhotographer.id,
                                previousPhotographer.name,
                                previousPhotographer.profileImageReference
                            );
                            setSelectedPhotographerId(previousPhotographer.id);
                        }
                    }}
                    rightDisabled={currentIndex === availablePhotographers.length - 1}
                    onRightClick={() => {
                        if (currentIndex < availablePhotographers.length - 1) {
                            let nextPhotographer = availablePhotographers[currentIndex + 1];
                            persistPhotographer(
                                nextPhotographer.id,
                                nextPhotographer.name,
                                nextPhotographer.profileImageReference
                            );
                            setSelectedPhotographerId(nextPhotographer.id);
                        }
                    }}
                    onChange={(photographerId: number) => {
                        let changedPhotographer = availablePhotographers.find(
                            (p) => p.id === photographerId
                        );

                        if (changedPhotographer) {
                            persistPhotographer(
                                changedPhotographer.id,
                                changedPhotographer.name,
                                changedPhotographer.profileImageReference
                            );
                        }

                        setSelectedPhotographerId(photographerId);
                    }}
                    onClickPortfolio={() => {
                        setShowPortfolioModal(true);
                    }}
                />
            ) : persistedPhotographer !== null ? (
                <PhotographerSection
                    showHint={showHints}
                    availablePhotographers={availablePhotographers}
                    disabled={showFooter}
                    photographer={{
                        name: persistedPhotographer.name,
                        profileImageReference: persistedPhotographer.avatarRef
                    }}
                    photographerIndicatorLabel="1/1"
                    leftDisabled={true}
                    rightDisabled={true}
                    onClickPortfolio={() => {
                        setShowPortfolioModal(true);
                    }}
                />
            ) : (
                <NoPhotographersAvailable />
            )}
            <BookingDateSelect
                showHint={showHints}
                disabled={showFooter}
                bookingDate={displayingBookingDate}
                onChange={(newDate: Date) => {
                    onChangeDisplayingBookingDate(newDate);
                }}
                earliestBookableDate={earliestBookableDate}
            />
            {availableTimeslotsPerHour.size < 1 ? (
                <NoAvailableTimeslots
                    photographerName={
                        selectedPhotographer
                            ? selectedPhotographer
                            : persistedPhotographer
                            ? persistedPhotographer.name
                            : ''
                    }
                />
            ) : (
                <AvailableTimeslotsSection
                    selectedDifferentTime={selectedTimeslot}
                    showHint={showHints}
                    availableTimeslotsPerHour={availableTimeslotsPerHour}
                    bottomOffset={heightOffset}
                    selectedTimeslot={selectedBookingDate}
                    onChange={handleChangeTimeslot}
                />
            )}
            <BookingFooter
                ref={footerRef}
                key={selectedBookingDate}
                availableTimeslots={availableTimeslotsForSelectedHour}
                forecast={selectedForecast}
                isOpen={showFooter}
                selectedDate={selectedBookingDate}
                selectedTimeslot={selectedTimeslot}
                onChangeTimeslot={handleChangeTimeslotFromFooter}
                selectedPhotographer={selectedPhotographer}
                onCancel={handleCancelBook}
                onBook={handleBookFactory(
                    planOrderId,
                    availablePhotographers.length > 0 && currentPhotographer
                        ? currentPhotographer.id
                        : null
                )}
            />
            {currentPhotographer !== null ? (
                <PortfolioModal
                    show={showPortfolioModal}
                    photographer={
                        availablePhotographers.length > 0 && currentPhotographer
                            ? {
                                  name: currentPhotographer.name,
                                  id: currentPhotographer.id
                              }
                            : { name: '', id: -1 }
                    }
                    onClose={() => {
                        setShowPortfolioModal(false);
                    }}
                />
            ) : null}
        </>
    );
}
